import type { FC, ImgHTMLAttributes, ReactEventHandler } from 'react'

import DOMPurify from 'dompurify'

import type { DomPurifyOptions } from './types'

const isLocalPath = (url: string): boolean =>
  url.startsWith('/') || url.startsWith('./') || url.startsWith('../') || url.startsWith('src/')

// Allow only safe protocols for absolute URLs
const getIsSafeURL = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin) // Use base URL for relative paths
    return ['https:', 'http:'].includes(parsed.protocol)
  } catch {
    return false
  }
}

// Sanitize attributes with DOMPurify
const sanitizeAttribute = (value: string | undefined, options: DomPurifyOptions = {}): string => {
  if (!value || typeof value !== 'string') return value ?? ''
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [], // No tags, only attributes
    ALLOWED_ATTR: ['src', 'alt', ...(options.ALLOWED_ATTR || [])],
    ...options,
  })
}

export const Image: FC<
  Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'onError'> & {
    src: string
    alt?: string
    fallbackSrc?: string
    className?: string
    onError?: ReactEventHandler<HTMLImageElement>
    domPurifyOptions?: DomPurifyOptions
  }
> = ({
  src,
  alt,
  fallbackSrc = '/default.png',
  className,
  onError,
  domPurifyOptions = {},
  ...rest
}) => {
  const sanitizedSrc = sanitizeAttribute(src, domPurifyOptions)
  // Allow local paths or safe absolute URLs; otherwise, use fallback
  const safeSrc =
    isLocalPath(sanitizedSrc) || getIsSafeURL(sanitizedSrc) ? sanitizedSrc : fallbackSrc
  const sanitizedAlt = sanitizeAttribute(alt, { ALLOWED_TAGS: [] })
  const safeOnError = typeof onError === 'function' ? onError : undefined

  return (
    <img src={safeSrc} alt={sanitizedAlt} className={className} onError={safeOnError} {...rest} />
  )
}
